<template>
  <b-card>
    <b-row>
      <b-col cols="6">
        <b-card>
          <b-row>
            <b-col cols="5">Select Template:</b-col>
            <b-col cols="7" class="text-left">
              <b-form-select class="modelSelect" :plain="true" :options="availableTemplateDocs" placeholder="Select Template" @change="fetchTemplates"></b-form-select>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="6">
        <b-card>
          <b-row>
            <b-col cols="5">Select Page:</b-col>
            <b-col cols="7" class="text-left">
              <b-form-select :plain="true" v-model="selectedTemplate.template" :options="templateOptions"></b-form-select>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
      <b-col cols="6">
        <b-card>
          <b-row>
            <b-col cols="5">Config Type:</b-col>
            <b-col cols="7" class="text-left">
              <b-form-select :plain="true" v-model="selectedTemplate.configType" :options="configTypes"></b-form-select>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <div align="center">
      <b-button type="button" class="button-style" variant="primary" @click="compareConfig" :disabled="!selectedTemplate.template || !selectedTemplate.configType"> Compare </b-button>
      <b-button type="button" class="button-style" variant="secondary" @click="updateConfigs"> Update </b-button>
    </div>

    <b-modal v-model="showComparisonModal" title="Configuration Comparison Results" size="xl" ok-only hide-footer class="comparison-modal">
      <div v-if="comparisonResult">
        <div class="diff-summary">
          <div>
            <b-badge variant="danger" class="mr-2" v-if="getPendingRemovals() > 0">
              {{ getPendingRemovals() }} removal{{ getPendingRemovals() !== 1 ? 's' : '' }}
            </b-badge>
            <b-badge variant="success" v-if="getPendingAdditions() > 0">
              {{ getPendingAdditions() }} addition{{ getPendingAdditions() !== 1 ? 's' : '' }}
            </b-badge>
            <b-badge variant="info" class="mr-2" v-if="getModifiedCount() > 0">
              {{ getModifiedCount() }} modification{{ getModifiedCount() !== 1 ? 's' : '' }}
            </b-badge>
          </div>
          <div class="diff-stats">
            <span class="lines-count">{{ getTotalLines() }} lines</span>
            <b-badge variant="warning" class="ml-3" v-if="getRejectedFieldsCount() > 0">
              {{ getRejectedFieldsCount() }} field{{ getRejectedFieldsCount() !== 1 ? 's' : '' }} reverted
            </b-badge>
          </div>
        </div>

        <b-alert variant="success" show v-if="comparisonResult.identical" class="mt-3">
          <strong>✓ No Differences Found!</strong> The stored file matches the existing configuration.
        </b-alert>
        
        <div v-else class="diff-container">
          <div class="diff-header">
            <div class="diff-column">
              <span class="diff-title removed-title">Current Configuration</span>
            </div>
            <div class="diff-column">
              <span class="diff-title added-title">Updated Configuration</span>
            </div>
          </div>

          <div class="diff-content">
            <div v-for="(line, index) in formattedDiff" :key="index" class="diff-line-wrapper">
              <div class="diff-line" :class="[line.type, { 'field-reverted': isFieldReverted(line.fieldId) }]">
                <div class="diff-column left-column">
                  <span class="line-number" :class="{'empty-line': !line.left}">{{ line.leftNumber }}</span>
                  <pre class="line-content" :class="{'highlight-removed': line.hasChange && line.fieldId && line.type === 'modified'}">{{ line.left }}</pre>
                </div>
                <div class="diff-column right-column">
                  <div class="line-number-container">
                    <span class="line-number" :class="{'empty-line': !line.right}">{{ line.rightNumber }}</span>
                    <div class="action-buttons" v-if="line.hasChange && line.fieldId">
                      <b-button 
                        v-if="!isFieldReverted(line.fieldId)"
                        size="sm" 
                        variant="outline-danger" 
                        class="reject-btn"
                        @click="rejectField(line.fieldId)"
                        v-b-tooltip.hover 
                        title="Revert this field to current value"
                      >
                        <i class="fa fa-times"></i>
                      </b-button>
                      <b-button 
                        v-else
                        size="sm" 
                        variant="outline-success" 
                        class="accept-btn"
                        @click="acceptField(line.fieldId)"
                        v-b-tooltip.hover 
                        title="Accept the updated value"
                      >
                        <i class="fa fa-check"></i>
                      </b-button>
                    </div>
                    <div class="action-buttons" v-else-if="line.hasChange && !line.fieldId && line.changeId">
                      <b-button 
                        v-if="!isChangeRejected(line.changeId)"
                        size="sm" 
                        variant="outline-danger" 
                        class="reject-btn"
                        @click="rejectChange(line.changeId)"
                        v-b-tooltip.hover 
                        title="Reject this change"
                      >
                        <i class="fa fa-times"></i>
                      </b-button>
                      <b-button 
                        v-else
                        size="sm" 
                        variant="outline-success" 
                        class="accept-btn"
                        @click="acceptChange(line.changeId)"
                        v-b-tooltip.hover 
                        title="Accept this change"
                      >
                        <i class="fa fa-check"></i>
                      </b-button>
                    </div>
                  </div>
                  <pre class="line-content" :class="getLineContentClass(line)">{{ getDisplayValue(line) }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer-actions mt-3">
          <b-button variant="outline-secondary" @click="resetAllChanges" class="mr-2">Reset All</b-button>
          <b-button variant="secondary" @click="updateConfigs">Update</b-button>
        </div>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'

export default {
  data () {
    return {
      selectedTemplate: {
        template: null,
        configType: null
      },
      availableTemplateDocs: [],
      templateOptions: [],
      configTypes: [
        {text: 'Template Mappings', value: 'temp_mappings'},
        {text: 'Service Mappings', value: 'service_mapings'},
        {text: 'Service Validation', value: 'service_validations'},
        {text: 'Custom Validation', value: 'custom_validations'}
      ],
      showComparisonModal: false,
      comparisonResult: null,
      formattedDiff: [],
      rejectedChanges: new Set(),
      rejectedFields: new Set(),
      changeGroups: new Map(),
      fieldChanges: new Map()
    }
  },

  computed: {
    ...mapGetters(['idToken', 'accountId', 'templateList'])
  },

  mounted () {
    this.getAllTemplaets()
  },

  methods: {
    getAllTemplaets () {
      Vue.axios.get('/template_docs/list_options', { headers: { Authorization: `Bearer ${this.idToken}` } })
        .then(response => {
          this.availableTemplateDocs = response.data.template_docs
        }, error => {
          if (error.status === 401) {
            Vue.toasted.show(this.$t('COMMON.MSG_EXPIRED'), { icon: 'chain-broken', type: 'error' })
          } else if (error.status === 500) {
            Vue.toasted.show(this.$t('COMMON.MSG_PROCESS_FAIL'), { icon: 'chain-broken', type: 'error' })
          }
        })
    },
    fetchTemplates (selectedTempDocID) {
      Vue.axios.get('/template_docs/' + selectedTempDocID + '/templates', { headers: {Authorization: `Bearer ${this.idToken}`} })
        .then(res => {
          this.templateOptions = res.data.templates.map(({ id, title }) => ({ value: id, text: title }))
        })
    },
    compareConfig () {
      if (!this.selectedTemplate.template) {
        Vue.toasted.show('Please select a template', { icon: 'exclamation-triangle', type: 'error' })
        return
      }
      if (!this.selectedTemplate.configType) {
        Vue.toasted.show('Please select a config type', { icon: 'exclamation-triangle', type: 'error' })
        return
      }

      Vue.axios.post('/templates/compare_config', {
        template: this.selectedTemplate.template,
        configType: this.selectedTemplate.configType
      }, {
        headers: {
          Authorization: `Bearer ${this.idToken}`,
          Accept: 'application/json'
        }
      })
        .then(response => {
          this.comparisonResult = response.data
          this.resetAllChanges()
          this.formatDiffView()
          this.showComparisonModal = true
        })
        .catch(error => {
          if (error.response && error.response.status === 401) {
            Vue.toasted.show(this.$t('COMMON.MSG_EXPIRED'), { icon: 'chain-broken', type: 'error' })
          } else if (error.response && error.response.status === 404) {
            Vue.toasted.show('Configuration file not found', { icon: 'exclamation-triangle', type: 'error' })
          } else if (error.response && error.response.status === 422) {
            Vue.toasted.show(error.response.data.error || 'Invalid configuration', { icon: 'exclamation-triangle', type: 'error' })
          } else {
            Vue.toasted.show(this.$t('COMMON.MSG_PROCESS_FAIL'), { icon: 'chain-broken', type: 'error' })
          }
        })
    },

    extractFieldName (line) {
      const match = line.match(/"([^"]+)"\s*:/)
      return match ? match[1] : null
    },

    formatDiffView () {
      const result = []
      let leftLineNum = 1
      let rightLineNum = 1
      let changeIdCounter = 0

      if (this.comparisonResult.removed && this.comparisonResult.removed.length > 0) {
        this.comparisonResult.removed.forEach(item => {
          const changeId = `removed-${changeIdCounter++}`
          const lines = JSON.stringify(item, null, 2).split('\n')
          this.changeGroups.set(changeId, {
            type: 'removed',
            data: item,
            lines: []
          })

          lines.forEach(line => {
            const lineData = {
              type: 'removed',
              left: line,
              right: '',
              leftNumber: leftLineNum++,
              rightNumber: '',
              hasChange: true,
              changeId: changeId,
              fieldId: null
            }
            result.push(lineData)
            this.changeGroups.get(changeId).lines.push(lineData)
          })
        })
      }

      if (this.comparisonResult.added && this.comparisonResult.added.length > 0) {
        this.comparisonResult.added.forEach(item => {
          const changeId = `added-${changeIdCounter++}`
          const lines = JSON.stringify(item, null, 2).split('\n')
          this.changeGroups.set(changeId, {
            type: 'added',
            data: item,
            lines: []
          })

          lines.forEach(line => {
            const lineData = {
              type: 'added',
              left: '',
              right: line,
              leftNumber: '',
              rightNumber: rightLineNum++,
              hasChange: true,
              changeId: changeId,
              fieldId: null
            }
            result.push(lineData)
            this.changeGroups.get(changeId).lines.push(lineData)
          })
        })
      }

      if (this.comparisonResult.modified && this.comparisonResult.modified.length > 0) {
        this.comparisonResult.modified.forEach(item => {
          const changeId = `modified-${changeIdCounter++}`
          // Build a set of changed field names from the changes array
          // Format: [["~", "field_name", old_value, new_value], ...]
          const changedFields = new Set()
          if (item.changes && Array.isArray(item.changes)) {
            item.changes.forEach(change => {
              if (Array.isArray(change) && change.length >= 2) {
                changedFields.add(change[1])
              }
            })
          }

          // Normalize both objects to have same key order
          const normalizedOriginal = this.normalizeObjectKeyOrder(item.original, item.updated)
          const normalizedUpdated = this.normalizeObjectKeyOrder(item.updated, item.original)
          const originalLines = JSON.stringify(normalizedOriginal, null, 2).split('\n')
          const updatedLines = JSON.stringify(normalizedUpdated, null, 2).split('\n')
          const maxLines = Math.max(originalLines.length, updatedLines.length)

          this.changeGroups.set(changeId, {
            type: 'modified',
            original: item.original,
            updated: item.updated,
            changes: item.changes,
            lines: [],
            fieldChanges: new Map()
          })

          for (let i = 0; i < maxLines; i++) {
            const leftLine = originalLines[i] || ''
            const rightLine = updatedLines[i] || ''
            // Extract field name from the line
            const fieldName = this.extractFieldName(rightLine) || this.extractFieldName(leftLine)
            // Check if this field is in the changed fields set
            const isFieldChanged = fieldName && changedFields.has(fieldName)
            // Determine if lines are different
            const linesAreDifferent = leftLine.trim() !== rightLine.trim()
            const lineType = linesAreDifferent && isFieldChanged ? 'modified' : 'unchanged'
            const hasChange = isFieldChanged

            let fieldId = null
            if (isFieldChanged && fieldName) {
              fieldId = `${changeId}-field-${fieldName}`
              this.fieldChanges.set(fieldId, {
                changeId: changeId,
                fieldName: fieldName,
                oldValue: leftLine,
                newValue: rightLine,
                leftLineNum: originalLines[i] ? leftLineNum : null,
                rightLineNum: updatedLines[i] ? rightLineNum : null
              })

              this.changeGroups.get(changeId).fieldChanges.set(fieldName, {
                fieldId: fieldId,
                oldValue: leftLine,
                newValue: rightLine
              })
            }

            const lineData = {
              type: lineType,
              left: leftLine,
              right: rightLine,
              leftNumber: originalLines[i] ? leftLineNum++ : '',
              rightNumber: updatedLines[i] ? rightLineNum++ : '',
              hasChange: hasChange,
              changeId: changeId,
              fieldId: fieldId
            }
            result.push(lineData)
            this.changeGroups.get(changeId).lines.push(lineData)
          }
        })
      }

      this.formattedDiff = result
    },

    normalizeObjectKeyOrder (obj1, obj2) {
      // Get all unique keys from both objects
      const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)])
      const normalized = {}

      // Sort keys alphabetically for consistent ordering
      const sortedKeys = Array.from(allKeys).sort()

      sortedKeys.forEach(key => {
        if (obj1.hasOwnProperty(key)) {
          normalized[key] = obj1[key]
        }
      })

      return normalized
    },

    getTotalLines () {
      return this.formattedDiff.length
    },

    getPendingRemovals () {
      let count = 0
      this.changeGroups.forEach((group, id) => {
        if (group.type === 'removed' && !this.rejectedChanges.has(id)) {
          count++
        }
      })
      return count
    },

    getPendingAdditions () {
      let count = 0
      this.changeGroups.forEach((group, id) => {
        if (group.type === 'added' && !this.rejectedChanges.has(id)) {
          count++
        }
      })
      return count
    },

    getModifiedCount () {
      let count = 0
      this.changeGroups.forEach((group, id) => {
        if (group.type === 'modified' && !this.rejectedChanges.has(id)) {
          let hasAnyAcceptedField = false
          if (group.fieldChanges) {
            group.fieldChanges.forEach((field, fieldName) => {
              if (!this.rejectedFields.has(field.fieldId)) {
                hasAnyAcceptedField = true
              }
            })
          }
          if (hasAnyAcceptedField) {
            count++
          }
        }
      })
      return count
    },

    getRejectedFieldsCount () {
      return this.rejectedFields.size
    },

    rejectChange (changeId) {
      this.rejectedChanges.add(changeId)
      this.$forceUpdate()
    },
    acceptChange (changeId) {
      this.rejectedChanges.delete(changeId)
      this.$forceUpdate()
    },
    rejectField (fieldId) {
      this.rejectedFields.add(fieldId)
      this.$forceUpdate()
    },

    acceptField (fieldId) {
      this.rejectedFields.delete(fieldId)
      this.$forceUpdate()
    },
    isChangeRejected (changeId) {
      return changeId && this.rejectedChanges.has(changeId)
    },

    isFieldReverted (fieldId) {
      return fieldId && this.rejectedFields.has(fieldId)
    },

    getDisplayValue (line) {
      if (line.fieldId && this.rejectedFields.has(line.fieldId)) {
        return line.left
      }
      return line.right
    },

    getLineContentClass (line) {
      if (line.fieldId && this.rejectedFields.has(line.fieldId)) {
        return ''
      }
      // Only highlight lines that have field changes (buttons)
      if (line.hasChange && line.fieldId && !this.rejectedFields.has(line.fieldId)) {
        return 'highlight-added'
      }
      if (line.type === 'added' && !this.isChangeRejected(line.changeId)) {
        return 'highlight-added'
      }
      return ''
    },
    resetAllChanges () {
      this.rejectedChanges.clear()
      this.rejectedFields.clear()
      this.changeGroups.clear()
      this.fieldChanges.clear()
      this.$forceUpdate()
    },
    buildMergedObject (original, updated, rejectedFieldIds) {
      const merged = JSON.parse(JSON.stringify(updated))
      rejectedFieldIds.forEach(fieldId => {
        const fieldInfo = this.fieldChanges.get(fieldId)
        if (fieldInfo && fieldInfo.fieldName) {
          const fieldName = fieldInfo.fieldName
          if (original.hasOwnProperty(fieldName)) {
            merged[fieldName] = original[fieldName]
          }
        }
      })
      return merged
    },

    updateConfigs () {
      const finalChanges = {
        removed: [],
        added: [],
        modified: []
      }
      this.changeGroups.forEach((group, changeId) => {
        if (group.type === 'removed' && !this.rejectedChanges.has(changeId)) {
          finalChanges.removed.push(group.data)
        } else if (group.type === 'added' && !this.rejectedChanges.has(changeId)) {
          finalChanges.added.push(group.data)
        } else if (group.type === 'modified' && !this.rejectedChanges.has(changeId)) {
          const rejectedFieldsInThisChange = []
          if (group.fieldChanges) {
            group.fieldChanges.forEach((field, fieldName) => {
              if (this.rejectedFields.has(field.fieldId)) {
                rejectedFieldsInThisChange.push(field.fieldId)
              }
            })
          }
          const mergedObject = this.buildMergedObject(
            group.original,
            group.updated,
            rejectedFieldsInThisChange
          )
          finalChanges.modified.push({
            original: group.original,
            merged: mergedObject,
            rejectedFields: rejectedFieldsInThisChange.map(fid => {
              const fieldInfo = this.fieldChanges.get(fid)
              return fieldInfo ? fieldInfo.fieldName : null
            }).filter(Boolean)
          })
        }
      })

      const totalChanges = finalChanges.removed.length + finalChanges.added.length + finalChanges.modified.length
      Vue.toasted.show(`${totalChanges} change(s) will be applied. Check console for details.`, {
        icon: 'info-circle',
        type: 'info',
        duration: 3000
      })

      let updateParams = {
        template: this.selectedTemplate.template,
        config_type: this.selectedTemplate.configType,
        changes: finalChanges
      }

      Vue.axios.post('/templates/update_configurations', updateParams, {
        headers: { Authorization: `Bearer ${this.idToken}` }
      })
        .then(response => {
          Vue.toasted.show('Configuration updated successfully', { icon: 'check', type: 'success' })
          this.showComparisonModal = false
        })
    }
  }
}
</script>

<style scoped>
.card {
  cursor: pointer;
  background: #ebeef3;
  border-radius: 10px;
}
.card .bar {
  width: 100%;
  height: 10px;
  border-radius: 10px 10px 0 0;
}
.card .card-header {
  border-radius: 10px 10px 0 0;
  background-color: #f7f7f7;
  padding: 1rem;
}

.diff-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f6f8fa;
  border: 1px solid #d0d7de;
  border-radius: 6px 6px 0 0;
  font-size: 14px;
}

.diff-stats {
  display: flex;
  align-items: center;
}

.lines-count {
  color: #57606a;
  font-weight: 500;
}

.diff-container {
  border: 1px solid #d0d7de;
  border-top: none;
  border-radius: 0 0 6px 6px;
  overflow: hidden;
}

.diff-header {
  display: flex;
  background-color: #f6f8fa;
  border-bottom: 1px solid #d0d7de;
  font-weight: 600;
  font-size: 12px;
}

.diff-header .diff-column {
  flex: 1;
  padding: 8px 10px;
  border-right: 1px solid #d0d7de;
}

.diff-header .diff-column:last-child {
  border-right: none;
}

.diff-title {
  color: #57606a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.removed-title::before {
  content: "−";
  color: #cf222e;
  margin-right: 5px;
  font-weight: bold;
}

.added-title::before {
  content: "+";
  color: #1a7f37;
  margin-right: 5px;
  font-weight: bold;
}

.diff-content {
  max-height: 600px;
  overflow-y: auto;
  background-color: #ffffff;
}

.diff-line-wrapper {
  border-bottom: 1px solid #d0d7de;
}

.diff-line-wrapper:last-child {
  border-bottom: none;
}

.diff-line {
  display: flex;
  font-size: 12px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.diff-line.removed {
  background-color: #ffebe9;
}

.diff-line.added {
  background-color: #dafbe1;
}

.diff-line.modified .left-column {
  background-color: #ffebe9;
}

.diff-line.modified .right-column {
  background-color: #dafbe1;
}

.diff-line.field-reverted .right-column {
  background-color: #fff8c5 !important;
}

.diff-line.field-reverted .line-content {
  font-style: italic;
  color: #6a737d;
}

.diff-column {
  flex: 1;
  display: flex;
  border-right: 1px solid #d0d7de;
}

.diff-column:last-child {
  border-right: none;
}

.left-column.diff-column {
  border-right: 1px solid #d0d7de;
}

.line-number-container {
  display: flex;
  align-items: center;
  position: relative;
}

.line-number {
  min-width: 50px;
  padding: 2px 10px;
  text-align: right;
  color: #57606a;
  background-color: #f6f8fa;
  user-select: none;
  border-right: 1px solid #d0d7de;
}

.line-number.empty-line {
  background-color: #f6f8fa;
}

.action-buttons {
  position: absolute;
  left: 50px;
  display: flex;
  gap: 4px;
  z-index: 10;
  background-color: white;
  padding: 2px;
  border-radius: 3px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

.reject-btn,
.accept-btn {
  padding: 2px 6px;
  font-size: 10px;
  line-height: 1;
  border-radius: 3px;
}

.reject-btn:hover {
  background-color: #cf222e;
  color: white;
}

.accept-btn:hover {
  background-color: #1a7f37;
  color: white;
}

.line-content {
  flex: 1;
  padding: 2px 10px;
  margin: 0;
  white-space: pre;
  overflow-x: auto;
  font-family: inherit;
  background-color: transparent;
  border: none;
}

.highlight-removed {
  background-color: #ffebe9;
}

.highlight-added {
  background-color: #dafbe1;
}

.modal-footer-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 15px;
  border-top: 1px solid #d0d7de;
}

.diff-content::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.diff-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.diff-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 5px;
}

.diff-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.comparison-modal >>> .modal-dialog {
  max-width: 96vw !important;
  width: 95vw !important;
  margin: 1.75rem auto;
}

.comparison-modal >>> .modal-body {
  padding: 1.5rem;
}
</style>



// WEBPACK FOOTER //
// src/components/settings/templateConfigBulkUpdate.vue